import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import type { PaymentMethodsType } from '@/constants/paymentMethods'

interface PaymentMethodItemTransferInProps extends PaymentMethodsType {
  onPress: (paymentMethod: PaymentMethodsType) => void
  isDisabled?: boolean
  hasOnlyOnePaymentMethod?: boolean
}

export const PaymentMethodItemTransferIn: React.FC<
  PaymentMethodItemTransferInProps
> = ({
  onPress,
  isDisabled = false,
  hasOnlyOnePaymentMethod = false,
  ...item
}) => {
  const theme = useTheme()

  const isWallet = Boolean(
    item.paymentMethod === 'Wallet' && item.displayCurrencyCode,
  )

  const handlePress = () => {
    if (!isDisabled) {
      onPress(item)
    }
  }

  // Специальное форматирование для transfer-in
  const formatTransferInBalance = () => {
    if (!item.formattedAmountBalance) return ''

    // Извлекаем числовую часть из formattedAmountBalance
    // Например: "$6.09" -> "6.09" или "1,234.56 USD" -> "1,234.56"
    const numericPart = item.formattedAmountBalance
      .replace(/[$€£¥₽,\s]/g, '')
      .replace(/[A-Z]{3}$/i, '')

    // Возвращаем в формате "X.XX USDT"
    return `${numericPart} ${item.displayCurrencyCode || 'USDT'}`
  }

  return (
    <BaseView
      row
      align="center"
      gap={16}
      py={16}
      style={{
        cursor: isDisabled ? 'default' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        transition: 'all 0.2s ease',
      }}
      className={!isDisabled ? 'hover:opacity-80' : ''}
      onClick={handlePress}
    >
      {/* Payment Method Icon */}
      {isWallet ? (
        <CardItem.CurrencyIcon
          uri={item.currencyIconUrl}
          size={48}
          bgColor={
            isDisabled
              ? theme.colors.background.screen
              : Array.isArray(item.currencyBgColor)
                ? item.currencyBgColor[0] || '#171819'
                : item.currencyBgColor || '#171819'
          }
          fallbackText={item.displayCurrencyCode?.substring(0, 2) || '?'}
        />
      ) : (
        <BaseView
          center
          style={{
            width: '62px',
            height: '50px',
            borderRadius: '16px',
            background:
              typeof item.bgIconContainer === 'string'
                ? item.bgIconContainer
                : Array.isArray(item.bgIconContainer)
                  ? `#171819`
                  : '#171819',
          }}
        >
          {item.iconName && (
            <AppIcons
              name={item.iconName}
              size={item.iconSize || 24}
              color="white"
            />
          )}
        </BaseView>
      )}

      {/* Payment Method Info - Специально для Transfer In */}
      <BaseView flex={1} gap={4}>
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          color={isDisabled ? 'label.disabled' : 'label.default'}
        >
          {/* Заменяем "Wallet" на "Balance" для transfer-in */}
          {isWallet ? `${item.currencyName} Balance` : item.name}
        </BaseText>
        {isWallet && (
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color={isDisabled ? 'label.disabled' : 'label.description'}
          >
            {/* Специальное форматирование: "6.09 USDT" вместо "$6.09" */}
            {formatTransferInBalance()}
          </BaseText>
        )}
      </BaseView>
    </BaseView>
  )
}

export default PaymentMethodItemTransferIn
